// src/lib/services/cfImagesService.ts
//
// Client wrapper for Cloudflare Images Direct Creator Uploads.
// Flow:
//   1. Call requestCfImageUpload_v1 — returns a one-time signed uploadURL.
//   2. POST the file as multipart/form-data to that URL.
//   3. Cloudflare responds with the final variant URL (imagedelivery.net/...).
//
// The CF Images API token stays server-side. This module never sees it.

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const requestUploadCallable = httpsCallable<
  { purpose: string },
  { id: string; uploadURL: string }
>(functions, 'requestCfImageUpload_v1');

const deleteImageCallable = httpsCallable<
  { imageId: string },
  { success: boolean }
>(functions, 'deleteCfImage_v1');

export interface CfImageUploadResult {
  /** Cloudflare Images image id (stable across variants). */
  id: string;
  /** Public variant URL suitable for <img src>. */
  url: string;
}

const readVariantsFromResult = (json: unknown): string[] => {
  if (!json || typeof json !== 'object') return [];
  const result = (json as { result?: unknown }).result;
  if (!result || typeof result !== 'object') return [];
  const variants = (result as { variants?: unknown }).variants;
  return Array.isArray(variants)
    ? variants.filter((v): v is string => typeof v === 'string')
    : [];
};

export async function uploadToCfImages(
  file: File,
  purpose: string,
): Promise<CfImageUploadResult> {
  const { data } = await requestUploadCallable({ purpose });
  const uploadURL = data?.uploadURL;
  const imageId = data?.id;
  if (!uploadURL || !imageId) {
    throw new Error('Cloudflare Images returned no upload URL');
  }

  const body = new FormData();
  body.append('file', file);

  const res = await fetch(uploadURL, { method: 'POST', body });
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  const success =
    !!json && typeof json === 'object' && (json as { success?: unknown }).success === true;
  if (!res.ok || !success) {
    throw new Error(`Cloudflare Images upload failed: HTTP ${res.status}`);
  }

  const variants = readVariantsFromResult(json);
  if (variants.length === 0) {
    throw new Error('Cloudflare Images returned no variants');
  }
  const publicVariant =
    variants.find((v) => v.endsWith('/public')) || variants[0];

  return { id: imageId, url: publicVariant };
}

/**
 * Delete a Cloudflare Images object by id.
 * Studio Library uses this when removing image assets from My Files.
 */
export async function deleteCfImage(imageId: string): Promise<void> {
  await deleteImageCallable({ imageId });
}
